import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useGetUserQuery, useUpdateUserMutation } from '../../api/peopleApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import EntityFormPage from '../../components/EntityFormPage/EntityFormPage';
import buttons from '../../styles/shared/buttons.module.css';

const ROLES = [
  { label: 'manager', value: 'manager' },
  { label: 'host', value: 'host' },
];

export default function UserEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();

  const { data: user, isLoading, isError, error } = useGetUserQuery(id);
  const [updateUser, { isLoading: isSaving }] = useUpdateUserMutation();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { name: '', email: '', role: 'manager' },
  });

  useEffect(() => {
    if (user) reset({ name: user.name, email: user.email, role: user.role });
  }, [user, reset]);

  useEffect(() => {
    if (isError) showToast('error', 'Users', errMsg(error, 'Could not load user.'));
  }, [isError, error, showToast]);

  const onSave = async (data) => {
    try {
      await updateUser({
        id,
        organizationId: user.organization_id,
        name: data.name.trim(),
        email: data.email.trim(),
        role: data.role,
      }).unwrap();
      showToast('success', 'Users', 'User updated.');
      navigate('/admin/users');
    } catch (err) {
      showToast(
        'error',
        'Users',
        err?.status === 409 ? 'Email already in use.' : errMsg(err, 'Could not save user.'),
      );
    }
  };

  if (isLoading) return <p className="textMuted">Loading…</p>;
  if (!user) return null;

  return (
    <EntityFormPage title="Edit user" backTo="/admin/users">
      <form onSubmit={handleSubmit(onSave)}>
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Role</label>
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={ROLES}
                className="w-full mb-3"
              />
            </div>
          )}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Name</label>
              <InputText id={field.name} className="w-full mb-3" {...field} />
            </div>
          )}
        />
        <Controller
          name="email"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Email</label>
              <InputText id={field.name} type="email" className="w-full mb-3" {...field} />
            </div>
          )}
        />
        <Button type="submit" label="Save" className={buttons.saveBtn} loading={isSaving} />
      </form>
    </EntityFormPage>
  );
}
